import { NameColumn } from '@/components/NameColumn';
import { getColumnWidth } from '@/components/table/columns/registry';
import type { Columns } from '@/components/table/types';
import { AssetStatusComponent } from '@/sections/asset/components/badges/AssetStatus';
import type { Webpage } from '@/types';

/**
 * Builds column definitions for Webpage entity tables
 *
 * Returns columns for:
 * - url: Webpage URL (primary display)
 * - status: Webpage status (Active/Inactive/Pending)
 * - service: Detected service name (if any)
 *
 * The url column uses NameColumn component to display:
 * - URL in bold purple text
 * - Service name below in smaller gray text (e.g., "okta", "ollama")
 *
 * @returns Column definitions for TanStack Table
 *
 * @example
 * const columns = buildWebpageColumns();
 * // Returns array with url, status, and service columns
 * // Each column has defensive null checks returning '-' for missing values
 */
export function buildWebpageColumns(): Columns<Webpage> {
  return [
    {
      id: 'status',
      label: 'Status',
      ...getColumnWidth('shared', 'status'),
      fixedWidth: 100,
      minWidth: 100,
      maxWidth: 100,
      cell: (item: Webpage) =>
        item?.status ? <AssetStatusComponent asset={item} /> : '-',
    },
    {
      id: 'url',
      label: 'URL',
      minWidth: 200,
      cell: (item: Webpage) => {
        if (!item?.url) {
          return '-';
        }
        const serviceDescription = item.service || undefined;
        return <NameColumn name={item.url} description={serviceDescription} />;
      },
    },
    {
      id: 'service',
      label: 'Service',
      ...getColumnWidth('shared', 'service'),
      sorting: false,
      cell: (item: Webpage) => {
        if (!item?.service) {
          return '-';
        }
        return item.service;
      },
    },
    {
      id: 'created',
      label: 'First Seen',
      ...getColumnWidth('shared', 'created'),
      cell: 'date' as const,
    },
    {
      id: 'visited',
      label: 'Last Seen',
      ...getColumnWidth('shared', 'visited'),
      cell: 'date' as const,
    },
  ];
}
